import {
  browserSupportsWebAuthn,
  getBrowserCapabilities,
  platformAuthenticatorIsAvailable,
  startAuthentication,
  startRegistration,
  WebAuthnError,
  type PublicKeyCredentialCreationOptionsJSON,
  type PublicKeyCredentialRequestOptionsJSON,
} from '@simplewebauthn/browser';
import type { Ergebnis } from '../../shared/api.js';
import type { PruefpunktKey } from '../../shared/pruefpunkte.js';
import { api, ApiFehler, fehlerText, modus } from '../api.js';
import { abschnitt, h, knopf, liste, meldung, setzeMeldung, zeile } from '../dom.js';
import { melde, uebernimmVomServer } from '../ergebnisse.js';
import type { Ansicht } from '../router.js';
import { lies } from '../speicher.js';
import { Aufraeumer, kopf, pruefpunktStand } from './gemeinsam.js';

/** Optionen werden vorab geladen, damit der Passkey-Dialog direkt im Tippen startet (Safari verlangt das). */
class VorabOptionen<T> {
  private laden: Promise<T> | undefined;
  private geladenAm = 0;

  constructor(private readonly holen: () => Promise<T>) {}

  vorladen(): void {
    this.geladenAm = Date.now();
    this.laden = this.holen();
    this.laden.catch(() => {});
  }

  /** Nimmt die vorab geladenen Optionen; jede Challenge gilt nur einmal. */
  nimm(): Promise<T> {
    const frisch = this.laden && Date.now() - this.geladenAm < 4 * 60_000;
    const optionen = frisch && this.laden ? this.laden : this.holen();
    this.laden = undefined;
    return optionen;
  }
}

export function clientFehlerText(fehler: unknown): string {
  if (fehler instanceof WebAuthnError) {
    if (fehler.code === 'ERROR_CEREMONY_ABORTED' || fehler.name === 'NotAllowedError') {
      return 'Abgebrochen oder vom Gerät nicht erlaubt (NotAllowedError).';
    }
    return `${fehler.name}: ${fehler.message}`;
  }
  return fehlerText(fehler);
}

/** Schickt die Antwort des Passkeys an den Server und übernimmt sein Ergebnis. */
export async function serverAntwort(pfad: string, json: unknown, status: HTMLElement): Promise<void> {
  try {
    const { ergebnis } = await api<{ ergebnis: Ergebnis }>(pfad, { json });
    await uebernimmVomServer([ergebnis]);
    setzeMeldung(status, ergebnis.text, ergebnis.status === 'ok' ? 'ok' : 'info');
  } catch (fehler) {
    const ergebnis = fehler instanceof ApiFehler ? (fehler.daten as { ergebnis?: Ergebnis } | undefined)?.ergebnis : undefined;
    if (ergebnis) await uebernimmVomServer([ergebnis]);
    setzeMeldung(status, fehlerText(fehler), 'fehler');
  }
}

/**
 * Anlegen und Anmelden mit Passkey; auch am Büro-PC genutzt. Bei „vom-handy“
 * kommt der Passkey per QR-Code und Bluetooth vom Handy.
 */
export async function passkeyBereich(aufraeumer: Aufraeumer, art: 'anlegen-und-anmelden' | 'vom-handy'): Promise<HTMLElement> {
  if (!browserSupportsWebAuthn()) {
    const keys: PruefpunktKey[] = art === 'vom-handy' ? ['passkey-vom-handy'] : ['passkey-anlegen', 'passkey-anmelden'];
    for (const key of keys) await melde(key, 'nicht_unterstuetzt', 'Dieser Browser kann keine Passkeys (WebAuthn fehlt).');
    return h('p', { class: 'meldung meldung-fehler' }, 'Dieser Browser kann keine Passkeys.');
  }

  const anmeldeOptionen = new VorabOptionen(async () => {
    const optionen = await api<PublicKeyCredentialRequestOptionsJSON>('/api/passkey/anmeldung/optionen', { methode: 'POST' });
    // Hinweis an den Browser, gleich den Weg übers Handy anzubieten (wird ignoriert, wo unbekannt).
    return art === 'vom-handy' ? { ...optionen, hints: ['hybrid' as const] } : optionen;
  });
  anmeldeOptionen.vorladen();

  if (art === 'vom-handy') {
    const status = meldung();
    return h(
      'div',
      {},
      pruefpunktStand('passkey-vom-handy', aufraeumer),
      knopf('Mit Passkey vom Handy anmelden', async () => {
        setzeMeldung(status, 'Im Dialog „iPhone, iPad oder Android-Gerät“ bzw. „Anderes Gerät“ wählen und den QR-Code mit dem Handy scannen.');
        try {
          const antwort = await startAuthentication({ optionsJSON: await anmeldeOptionen.nimm() });
          await serverAntwort('/api/passkey/anmeldung', { antwort, zweck: 'vom-handy' }, status);
        } catch (fehler) {
          const text = clientFehlerText(fehler);
          await melde('passkey-vom-handy', 'fehler', text, { fehler: (fehler as Error).name });
          setzeMeldung(status, text, 'fehler');
        }
        anmeldeOptionen.vorladen();
      }),
      status,
    );
  }

  const name = h('input', { id: 'passkey-name', name: 'passkey-name', type: 'text', maxlength: 60, autocomplete: 'off', value: `${(await lies('name')) ?? 'Test'} (${modus() === 'app' ? 'App' : 'Browser'})` });
  const registrierOptionen = new VorabOptionen(() =>
    api<PublicKeyCredentialCreationOptionsJSON>('/api/passkey/registrierung/optionen', { json: { name: name.value.trim() || 'Test' } }),
  );
  registrierOptionen.vorladen();
  let verzoegert: number | undefined;
  name.addEventListener('input', () => {
    window.clearTimeout(verzoegert);
    verzoegert = window.setTimeout(() => registrierOptionen.vorladen(), 400);
  });
  aufraeumer.dazu(() => window.clearTimeout(verzoegert));

  const statusAnlegen = meldung();
  const statusAnmelden = meldung();

  return h(
    'div',
    { class: 'stapel' },
    h(
      'div',
      { class: 'teilbereich' },
      pruefpunktStand('passkey-anlegen', aufraeumer),
      h('div', { class: 'feld' }, h('label', { for: 'passkey-name' }, 'Name der Testperson'), name),
      knopf('Passkey anlegen', async () => {
        try {
          const antwort = await startRegistration({ optionsJSON: await registrierOptionen.nimm() });
          await serverAntwort('/api/passkey/registrierung', { antwort }, statusAnlegen);
        } catch (fehler) {
          const text = clientFehlerText(fehler);
          await melde('passkey-anlegen', 'fehler', text, { fehler: (fehler as Error).name, modus: modus() });
          setzeMeldung(statusAnlegen, text, 'fehler');
        }
        registrierOptionen.vorladen();
      }),
      statusAnlegen,
    ),
    h(
      'div',
      { class: 'teilbereich' },
      pruefpunktStand('passkey-anmelden', aufraeumer),
      knopf('Mit Passkey anmelden', async () => {
        try {
          const antwort = await startAuthentication({ optionsJSON: await anmeldeOptionen.nimm() });
          await serverAntwort('/api/passkey/anmeldung', { antwort, zweck: 'anmelden' }, statusAnmelden);
        } catch (fehler) {
          const text = clientFehlerText(fehler);
          await melde('passkey-anmelden', 'fehler', text, { fehler: (fehler as Error).name, modus: modus() });
          setzeMeldung(statusAnmelden, text, 'fehler');
        }
        anmeldeOptionen.vorladen();
      }),
      statusAnmelden,
    ),
  );
}

export async function faehigkeiten(): Promise<HTMLDListElement> {
  const plattform = browserSupportsWebAuthn() ? await platformAuthenticatorIsAvailable() : false;
  const f = browserSupportsWebAuthn() ? await getBrowserCapabilities().catch(() => undefined) : undefined;
  const text = (wert: string | undefined) => (wert === 'supported' ? 'ja' : wert === 'unsupported' ? 'nein' : 'unbekannt');
  return liste(
    zeile('Passkeys im Browser', browserSupportsWebAuthn() ? 'ja' : 'nein'),
    zeile('Geräte-Entsperrung (Face ID, Fingerabdruck, Windows Hello)', plattform ? 'ja' : 'nein'),
    zeile('Passkey von einem anderen Gerät (QR-Code)', text(f?.hybridTransport)),
    zeile('Läuft als', modus() === 'app' ? 'installierte App' : 'Browser'),
  );
}

export const passkey: Ansicht = async (ziel) => {
  const aufraeumer = new Aufraeumer();
  ziel.append(
    ...kopf('Passkey', 'Passkey in der installierten App anlegen und damit anmelden. Scheitert das, wird Passwort plus App-Code der Hauptweg (Konzept, Anmeldung).'),
    abschnitt('Anlegen und anmelden', await passkeyBereich(aufraeumer, 'anlegen-und-anmelden')),
    abschnitt('Was der Browser meldet', await faehigkeiten()),
    h(
      'a',
      { href: '/pruefung/anmeldefeld', class: 'karte hinweis verweis' },
      h('strong', {}, 'Als Nächstes:'),
      ' Bietet das Gerät den Passkey gleich im Anmeldefeld an?',
      h('span', { 'aria-hidden': 'true' }, ' →'),
    ),
    abschnitt(
      'Hinweis',
      h('p', {}, `Jeder Test legt einen neuen Passkey für ${location.hostname} an. Nach dem Test lassen sie sich in den Passwort-Einstellungen des Geräts löschen.`),
    ),
  );
  return aufraeumer.alles;
};
